/**
 * AudioPlayerContext.jsx
 * 
 * Global audio player state management for the Winery Finder app.
 * Provides a Spotify-style audio player experience for audio tours.
 * 
 * Features:
 * - Play/pause/stop audio tracks
 * - Preview mode (30-second clips) vs full mode (purchased)
 * - MiniPlayer (bottom bar) and FullScreenPlayer support
 * - Volume and playback speed controls
 * - Track progress and seeking
 * 
 * Usage:
 *   import { useAudioPlayer } from '@/contexts/AudioPlayerContext';
 *   const { playTrack, togglePlayPause, closePlayer } = useAudioPlayer();
 * 
 * @module AudioPlayerContext
 * @version 1.0.0
 * @lastUpdated March 27, 2026
 */

import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { cacheAudioTour, checkCacheStatus } from '@/utils/audioCache';
import api from '@/lib/api';
import { getTokenForTour } from '@/lib/guestPurchases';

/**
 * Convert an audio URL to use the backend proxy for external URLs.
 * This bypasses hotlink protection and CORS issues on external audio servers.
 * URLs already pointing to our own API (/api/files/) are left unchanged.
 */
const getProxiedAudioUrl = (url) => {
  if (!url) return url;
  // Already a local/API URL - no proxy needed
  if (url.startsWith('/api/') || url.includes('/api/files/') || url.includes('/api/audio-proxy')) {
    return url;
  }
  // External URL - route through our proxy (relative path, same-origin via Nginx)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return `/api/audio-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

/**
 * Resolve the source URL to feed into the <audio> element.
 *
 * For PURCHASED (non-preview) tours we request a short-lived signed URL
 * from the backend rather than exposing the raw audio_url. The signed
 * URL is HMAC-signed, bound to the buyer's IP /24 + UA, and valid for
 * 1 hour. If it expires mid-playback (rare), the `<audio>` error handler
 * automatically fetches a fresh one and resumes.
 *
 * Preview clips are unchanged — they don't need signing.
 *
 * Returns { src, isSigned } — src is what audio.src should be set to,
 * isSigned=true means the URL was signed and can be auto-refreshed.
 */
/**
 * Ask the backend for a fresh signed URL. `customer:<tour_id>` tokens are
 * checked against the signed-in customer, so send their token with it
 * (the shared api client only attaches the admin token).
 */
export const fetchSignedAudio = async (purchaseToken) => {
  const customerToken = localStorage.getItem('customer_token');
  const headers = purchaseToken.startsWith('customer:') && customerToken
    ? { Authorization: `Bearer ${customerToken}` }
    : undefined;
  const res = await api.get(
    `/audio-tours/access/${encodeURIComponent(purchaseToken)}/signed-url`,
    headers ? { headers } : undefined,
  );
  return res.data;
};

const resolveAudioSrc = async (track) => {
  // Previews always use the direct URL
  if (track.is_preview) {
    return { src: getProxiedAudioUrl(track.audio_url), isSigned: false };
  }
  // Non-preview: try to get a signed URL. Falls back to proxy if the
  // signing endpoint isn't reachable (e.g. old server before deploy).
  const purchaseToken = getTokenForTour(track.tour_id) ||
                        (localStorage.getItem('customer_token') ? `customer:${track.tour_id}` : null);
  if (!purchaseToken) {
    return { src: getProxiedAudioUrl(track.audio_url), isSigned: false };
  }
  try {
    const signed = await fetchSignedAudio(purchaseToken);
    return { src: signed.url, isSigned: true, purchaseToken, fileUrl: signed.file_url };
  } catch {
    return { src: getProxiedAudioUrl(track.audio_url), isSigned: false };
  }
};

/** @type {React.Context} Audio player context */
const AudioPlayerContext = createContext(null);

/**
 * Custom hook to access audio player context
 * @returns {Object} Audio player state and methods
 * @throws {Error} If used outside AudioPlayerProvider
 */
export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

/**
 * Audio Player Provider Component
 * Wraps the app to provide global audio state
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const AudioPlayerProvider = ({ children }) => {
  /** @type {React.RefObject<HTMLAudioElement>} Reference to HTML5 audio element */
  const audioRef = useRef(null);
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  /** @type {Object|null} Current track metadata (title, artist, image, urls) */
  const [currentTrack, setCurrentTrack] = useState(null);
  
  /** @type {boolean} Whether audio is currently playing */
  const [isPlaying, setIsPlaying] = useState(false);
  
  /** @type {number} Total duration of current track in seconds */
  const [duration, setDuration] = useState(0);
  
  /** @type {number} Current playback position in seconds */
  const [currentTime, setCurrentTime] = useState(0);
  
  /** @type {boolean} Loading state while audio buffers */
  const [isLoading, setIsLoading] = useState(false);
  
  /** @type {boolean} Whether MiniPlayer is visible */
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  
  /** @type {boolean} Whether FullScreenPlayer is open */
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  /** @type {number} Volume level 0-1 */
  const [volume, setVolume] = useState(1);
  
  /** @type {number} Playback speed multiplier (0.5, 1, 1.5, 2) */
  const [playbackRate, setPlaybackRate] = useState(1);
  /** Map of tour_id → true once the audio is cached in the Service Worker.
      Populated by the auto-cache path in playTrack. Consumed by UI badges. */
  const [offlineReady, setOfflineReady] = useState({});

  // ============================================
  // AUDIO ELEMENT INITIALIZATION
  // ============================================
  
  /**
   * Initialize HTML5 Audio element and set up event listeners
   * Handles: loading, playback, time updates, and errors
   */
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.preload = 'metadata';
    
    const audio = audioRef.current;
    
    // Event handlers for audio element
    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handleError = async (e) => {
      // If this is a signed URL that just expired, transparently fetch
      // a fresh one and resume from where we were. Only try once per
      // playback session to avoid infinite loops on hard errors.
      const isSigned = audio.dataset.isSigned === '1';
      const purchaseToken = audio.dataset.purchaseToken;
      const alreadyRefreshed = audio.dataset.sigRefreshed === '1';
      if (isSigned && purchaseToken && !alreadyRefreshed) {
        try {
          audio.dataset.sigRefreshed = '1';
          const currentPos = audio.currentTime || 0;
          const signed = await fetchSignedAudio(purchaseToken);
          audio.src = signed.url;
          audio.load();
          // Restore position after the fresh src loads
          const onceReady = () => {
            audio.currentTime = currentPos;
            audio.play().catch(() => {});
            audio.removeEventListener('canplay', onceReady);
          };
          audio.addEventListener('canplay', onceReady);
          return;
        } catch {
          // fall through to error UI
        }
      }
      console.error('Audio error:', e);
      setIsLoading(false);
      setIsPlaying(false);
    };

    // Attach event listeners
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Cleanup on unmount
    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Update volume when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Update playback rate when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const playTrack = useCallback(async (track, openFullScreen = false) => {
    if (!audioRef.current || !track?.audio_url) return;

    try {
      setIsLoading(true);
      
      // If same track, just toggle play/pause
      if (currentTrack?.tour_id === track.tour_id) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          await audioRef.current.play();
          setIsPlaying(true);
        }
        // Open full screen if requested
        if (openFullScreen) {
          setIsFullScreen(true);
        }
        setIsLoading(false);
        return;
      }

      // New track
      setCurrentTrack(track);
      setIsPlayerVisible(true);
      
      // Open full screen if requested (for purchased tours)
      if (openFullScreen) {
        setIsFullScreen(true);
      }
      
      // Get either a signed URL (for owned tours) or a proxied URL (for previews).
      // Signed URLs expire in 1h but auto-refresh on error is wired below.
      const { src: audioSrc, isSigned, purchaseToken, fileUrl } = await resolveAudioSrc(track);
      audioRef.current.src = audioSrc;
      audioRef.current.dataset.isSigned = isSigned ? '1' : '0';
      audioRef.current.dataset.purchaseToken = purchaseToken || '';
      audioRef.current.dataset.trackId = track.tour_id || '';
      audioRef.current.dataset.sigRefreshed = '0';  // reset auto-refresh guard for new track
      audioRef.current.load();
      
      await audioRef.current.play();
      setIsPlaying(true);

      // Auto-cache purchased (non-preview) tours for offline playback.
      // Fetches via the signed file URL (paid audio isn't served without
      // one); the service worker keys the cache by path only, so cached
      // bytes stay valid after the signature expires.
      if (!track.is_preview && track.tour_id) {
        cacheAudioTour(fileUrl || track.audio_url, track.tour_id)
          .then(() => setOfflineReady((prev) => ({ ...prev, [track.tour_id]: true })))
          .catch(() => {});
      }
    } catch (error) {
      console.error('Error playing track:', error);
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentTrack, isPlaying]);

  const togglePlayPause = useCallback(async () => {
    if (!audioRef.current || !currentTrack) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Toggle play/pause error:', error);
    }
  }, [isPlaying, currentTrack]);

  const seekTo = useCallback((time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(time, duration));
      setCurrentTime(audioRef.current.currentTime);
    }
  }, [duration]);

  const skipForward = useCallback((seconds = 15) => {
    seekTo(currentTime + seconds);
  }, [currentTime, seekTo]);

  const skipBackward = useCallback((seconds = 15) => {
    seekTo(currentTime - seconds);
  }, [currentTime, seekTo]);

  const stopTrack = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      // Remove src after pause to avoid "Audio error: Event" from aborted load
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    setCurrentTrack(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsPlayerVisible(false);
    setIsFullScreen(false);
  }, []);

  const showPlayer = useCallback(() => {
    setIsPlayerVisible(true);
  }, []);

  const hidePlayer = useCallback(() => {
    stopTrack();
  }, [stopTrack]);

  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => !prev);
  }, []);

  const value = {
    // State
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    isLoading,
    isPlayerVisible,
    isFullScreen,
    volume,
    playbackRate,
    offlineReady,
    
    // Actions
    playTrack,
    togglePlayPause,
    seekTo,
    skipForward,
    skipBackward,
    stopTrack,
    showPlayer,
    hidePlayer,
    toggleFullScreen,
    setVolume,
    setPlaybackRate,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export default AudioPlayerContext;
